import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages';
import {
  bankAccountsValidationSchema,
  fundingSourcesObject,
  monthlyInflowsValid,
  multipleOfZero,
  numberWithoutLeadingZero,
  positiveNumberFop,
} from './validationSchema.ts';

export const financialConditionSoleTraderValidationSchema = Yup.object().shape({
  noOtherBankAccounts: Yup.boolean().nullable(),
  bankAccounts: Yup.mixed().when('noOtherBankAccounts', {
    is: true,
    then: () => Yup.array().optional().notRequired(),
    otherwise: () => bankAccountsValidationSchema,
  }),
  prevDate: Yup.object()
    .shape({
      value: Yup.string().nullable(),
      label: Yup.string().required(errorMessages.FIELD_REQUIRED),
    })
    .nullable()
    .required(errorMessages.FIELD_REQUIRED),
  plannedMonthlyInflows: positiveNumberFop.test(
    'greater-than-zero',
    errorMessages.MUST_BE_GREATER_THAN_ZERO,
    (value) => {
      if (!value) return false;
      if (Number.isNaN(+value)) return true;
      return +value > 0;
    }
  ),
  prevYearTaxableIncome: Yup.string()
    .required(errorMessages.FIELD_REQUIRED)
    .test('no-comma', errorMessages.USE_COMMA, (value) => !value?.includes(','))
    .test('only-digits-and-dot', errorMessages.MUST_BE_NUMBER, (value) => {
      if (!value) return false;
      if (!/^-?\d*\.?\d*$/.test(value)) return false;
      if ((value.match(/\./g) || []).length > 1) return false;
      if ((value.match(/-/g) || []).length > 1) return false;
      if (value.includes('-') && !value.startsWith('-')) return false;
      return true;
    })
    .test('no-leading-zeros', errorMessages.MUST_BE_WITHOUT_ZEROES, (value) => {
      if (!value) return false;
      return !/^0\d/.test(value);
    })
    .test('max-2-decimals', errorMessages.MAX_LENGTH_OF_CHARACTERS(2), (value) => {
      if (!value) return false;
      const parts = value.split('.');
      const dec = parts[1];
      if (dec && dec.length > 2) return false;
      return true;
    }),
  maxMonthlyInflows: positiveNumberFop.test(
    'greater-than-zero',
    errorMessages.MUST_BE_GREATER_THAN_ZERO,
    (value) => {
      if (!value) return false;
      if (Number.isNaN(+value)) return true;
      return +value > 0;
    }
  ),
  fundingSources: fundingSourcesObject,
});

export const financialConditionLegalResidentValidationSchema = Yup.object().shape({
  noOtherBankAccounts: Yup.boolean().nullable(),
  bankAccounts: Yup.mixed().when('noOtherBankAccounts', {
    is: true,
    then: () => Yup.array().optional().notRequired(),
    otherwise: () => bankAccountsValidationSchema,
  }),
  prevDate: Yup.object()
    .shape({
      value: Yup.string().nullable(),
      label: Yup.string().required(errorMessages.FIELD_REQUIRED),
    })
    .nullable()
    .required(errorMessages.FIELD_REQUIRED),
  securitiesNominalValue: multipleOfZero,
  netIncomeFromSales: numberWithoutLeadingZero,
  totalLoanDebt: multipleOfZero,
  debtFromNonResidentLoans: multipleOfZero,
  costOfGoodsSold: numberWithoutLeadingZero,
  grossProfitOrLoss: numberWithoutLeadingZero,
  nonCurrentAssets: multipleOfZero,
  totalDeposits: multipleOfZero,
  currentAssets: multipleOfZero,
  currentLiabilities: multipleOfZero,
  equity: numberWithoutLeadingZero,
  salesExpenses: numberWithoutLeadingZero,
  plannedMonthlyInflows: monthlyInflowsValid,
  maxMonthlyInflows: monthlyInflowsValid,
  fundingSources: fundingSourcesObject,
});
